import { ArrowDown, ChevronDown } from "lucide-react";
import type { AuctionRecord } from "../../lib/auctions";
import { dutchAuctionPresentation, dutchCountdown } from "./dutch-auction-presentation";
import "./dutch-auction.css";

const integer = new Intl.NumberFormat("en-GB");
const bitcoin = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 8 });
const sats = (value: number) => `${integer.format(value)} sats`;
const date = (value: number) => new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "medium" }).format(value);

export function DutchAuctionPrice({ auction, now }: { auction: AuctionRecord; now: number }) {
  const view = dutchAuctionPresentation(auction, now);
  const { phase, price, nextDrop } = view;
  const state = phase === "scheduled" ? "Opening soon" : phase === "accepted" ? "Purchase accepted"
    : phase === "closed" ? auction.status === "cancelled" ? "Cancelled" : "No longer available"
      : phase === "floor" ? "At the artist’s floor" : "Price descending";
  const label = phase === "accepted" ? "Accepted purchase price" : phase === "scheduled" ? "Starting price"
    : phase === "closed" ? "Last asking price" : "Current price";
  return <section className="ar-dutch-price" aria-label="Dutch auction price">
    <header className="ar-dutch-heading">
      <span>Dutch auction</span>
      <span className="ar-dutch-state">{view.active && phase !== "scheduled" ? <span className="ar-dutch-live-dot" aria-hidden="true" /> : null}{state}</span>
    </header>
    <div className="ar-dutch-prices">
      <div className="ar-dutch-current">
        <span className="ar-dutch-label">{label}</span>
        <strong>{bitcoin.format(price / 100_000_000)} <small>BTC</small></strong>
        <span className="ar-dutch-sats">{sats(price)}</span>
      </div>
      {nextDrop ? <div className="ar-dutch-next">
        <span className="ar-dutch-label">Next price <ArrowDown aria-hidden="true" /></span>
        <strong>{bitcoin.format(nextDrop.price / 100_000_000)} <small>BTC</small></strong>
        <span className="ar-dutch-sats">{sats(nextDrop.price)}</span>
        <span className="ar-dutch-countdown">Drops in <b>{dutchCountdown(nextDrop.at, now)}</b></span>
      </div> : <div className="ar-dutch-next ar-dutch-note">
        <span className="ar-dutch-label">{phase === "scheduled" ? "Opens in" : phase === "floor" ? "No further drops" : phase === "accepted" ? "Next step" : "Price schedule stopped"}</span>
        {phase === "scheduled" ? <strong>{dutchCountdown(auction.startsAt, now)}</strong>
          : <p>{phase === "floor" ? "The floor price holds until closing." : phase === "accepted" ? "Follow settlement below." : "This work is not accepting purchases."}</p>}
      </div>}
    </div>
  </section>;
}

export function DutchAuctionSchedule({ auction, now }: { auction: AuctionRecord; now: number }) {
  const view = dutchAuctionPresentation(auction, now);
  const { nextDrop } = view;
  return <section className="ar-dutch-schedule" aria-label="Dutch price schedule">
      <div className="ar-dutch-chart-heading"><span>Price schedule</span><span>Minute-by-minute pricing</span></div>
      <svg className="ar-dutch-chart" viewBox="0 0 600 120" aria-hidden="true" focusable="false">
        <defs><clipPath id={`dutch-progress-${auction.id}`}><rect x="0" y="0" width={view.cursor.x} height="120" /></clipPath></defs>
        <path className="ar-dutch-chart-guide" d="M8,104 H592" />
        <path className="ar-dutch-chart-future" d={view.path} />
        <path className="ar-dutch-chart-past" d={view.path} clipPath={`url(#dutch-progress-${auction.id})`} />
        <path className="ar-dutch-chart-guide" d={`M${view.cursor.x},${view.cursor.y} V112`} />
        <circle cx={view.cursor.x} cy={view.cursor.y} r="5" />
      </svg>
      <div className="ar-dutch-endpoints"><span>Start <b>{sats(auction.startPriceSats)}</b></span><span>Floor <b>{sats(auction.floorPriceSats!)}</b></span></div>
      <details className="ar-dutch-details">
        <summary>View timing & how it works <ChevronDown aria-hidden="true" /></summary>
        <dl>
          <div><dt>Opens</dt><dd>{date(auction.startsAt)}</dd></div>
          {nextDrop ? <div><dt>Next price drop</dt><dd>{sats(nextDrop.price)} · {date(nextDrop.at)}</dd></div> : null}
          {view.floorAt < auction.endsAt ? <div><dt>Scheduled floor</dt><dd>{date(view.floorAt)}</dd></div> : null}
          <div><dt>Closes</dt><dd>{date(auction.endsAt)}</dd></div>
        </dl>
        <p>The asking price updates in one-minute steps, never below the artist’s floor. Buy at the current price or wait for a lower one—another collector may buy first.</p>
        <p>This is a schedule overview, not a price reservation. Your exact price and Bitcoin network fee are confirmed in the purchase review before you sign. No lower-price bids are queued.</p>
      </details>
  </section>;
}
